// src/pages/Home.js
import React from 'react';
import styled from 'styled-components';
import Icon from '../components/Icon';

const HomeContainer = styled.div`
  max-width: 900px;
`;

const WelcomeCard = styled.div`
  background-color: var(--ink);
  border-radius: var(--radius-md);
  padding: 40px;
  color: var(--white);
  margin-bottom: 32px;

  h1 {
    margin: 0 0 12px 0;
    color: var(--white);
  }

  p {
    font-size: 18px;
    opacity: 0.85;
    margin: 0;
    line-height: 1.6;
    color: var(--white);
  }
`;

const Section = styled.section`
  background: var(--paper-raised);
  border-radius: var(--radius-md);
  padding: 28px;
  margin-bottom: 24px;
  border: 1px solid var(--line);
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--accent-ink);
`;

const SectionContent = styled.div`
  color: var(--ink-soft);
  font-size: 15px;
  line-height: 1.7;

  p {
    margin: 0 0 12px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin: 12px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: var(--ink);
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const FeatureItem = styled.div`
  background: var(--paper);
  border-radius: var(--radius-md);
  padding: 16px;

  .icon {
    color: var(--accent-ink);
    margin-bottom: 8px;
  }

  .title {
    font-weight: 600;
    color: var(--ink);
    font-size: 14px;
    margin-bottom: 4px;
  }

  .desc {
    font-size: 13px;
    color: var(--ink-faint);
  }
`;

const DisclaimerCard = styled(Section)`
  background: var(--paper);
  border: 1px solid var(--line);
`;

const ContactCard = styled(Section)`
  background: var(--ink);
  color: var(--white);

  ${SectionTitle} {
    color: var(--white);
  }

  ${SectionContent} {
    color: var(--line-strong);

    strong {
      color: var(--white);
    }

    .email a {
      color: var(--accent);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ServicesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const ServiceTag = styled.span`
  font-family: 'IBM Plex Mono', monospace;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  color: var(--white);
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 20px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius-md);

  .name {
    font-weight: 600;
    font-size: 16px;
    color: var(--white);
    margin-bottom: 4px;
  }

  .email {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 13px;
  }
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: var(--radius-sm);
  background-color: var(--accent);
  color: var(--ink);
  font-weight: 600;
  font-size: 13px;
  border: 1.5px solid var(--accent);
  transition: background-color var(--transition-fast), border-color var(--transition-fast);
  white-space: nowrap;

  &:hover {
    background-color: var(--accent-tint);
    border-color: var(--accent-tint);
    text-decoration: none;
  }
`;

const Home = () => {
  return (
    <HomeContainer>
      <WelcomeCard>
        <h1>Welcome to Algolyze</h1>
        <p>
          Your all-in-one toolkit for managing Algolia indexes. Export data, clone indexes, 
          compare configurations, and much more — all from a single, intuitive interface.
        </p>
      </WelcomeCard>

      <Section>
        <SectionTitle>
          <Icon name="tool" size={20} /> What can you do with Algolyze?
        </SectionTitle>
        <SectionContent>
          <p>
            Algolyze provides a suite of powerful tools to help you manage your Algolia indexes
            more efficiently, without writing a single line of code.
          </p>
          <FeatureGrid>
            <FeatureItem>
              <div className="icon"><Icon name="upload" size={22} /></div>
              <div className="title">Export Data</div>
              <div className="desc">Export records by filters or attributes to CSV</div>
            </FeatureItem>
            <FeatureItem>
              <div className="icon"><Icon name="clone" size={22} /></div>
              <div className="title">Clone Indexes</div>
              <div className="desc">Duplicate indexes with all settings & rules</div>
            </FeatureItem>
            <FeatureItem>
              <div className="icon"><Icon name="bar-chart" size={22} /></div>
              <div className="title">Compare Configs</div>
              <div className="desc">Diff searchable attributes, facets & rules</div>
            </FeatureItem>
            <FeatureItem>
              <div className="icon"><Icon name="edit" size={22} /></div>
              <div className="title">Bulk Updates</div>
              <div className="desc">Update records in batch via CSV upload</div>
            </FeatureItem>
            <FeatureItem>
              <div className="icon"><Icon name="search" size={22} /></div>
              <div className="title">Analytics</div>
              <div className="desc">Find searches with no results</div>
            </FeatureItem>
            <FeatureItem>
              <div className="icon"><Icon name="thumbs-up" size={22} /></div>
              <div className="title">Recommend Tester</div>
              <div className="desc">Test your recommendation models</div>
            </FeatureItem>
          </FeatureGrid>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Icon name="lock" size={20} /> Security & Privacy
        </SectionTitle>
        <SectionContent>
          <p>
            <strong>Your data stays on your machine.</strong> Algolyze runs entirely in your browser. 
            Your API keys and data are never sent to our servers — all operations are performed 
            locally using direct calls to the Algolia API.
          </p>
          <p>
            We do not store, log, or have access to any of your credentials or index data. 
            When you close your browser tab, everything is cleared.
          </p>
        </SectionContent>
      </Section>

      <DisclaimerCard>
        <SectionTitle>
          <Icon name="alert-triangle" size={20} /> Disclaimer
        </SectionTitle>
        <SectionContent>
          <p>
            <strong>Use at your own risk.</strong> Algolyze is provided "as is" without warranty of any kind. 
            We are not responsible for any data loss, corruption, or unintended modifications 
            to your Algolia indexes resulting from the use of this tool.
          </p>
          <p>
            Always double-check your actions, especially when using destructive features like 
            "Delete Objects" or "Clone Index" (which overwrites the target).
          </p>
          <p>
            <strong>Not affiliated with Algolia.</strong> This tool is an independent project and is not 
            endorsed by, affiliated with, or supported by Algolia, Inc.
          </p>
        </SectionContent>
      </DisclaimerCard>

      <ContactCard>
        <SectionTitle>
          <Icon name="rocket" size={20} /> Need a custom solution?
        </SectionTitle>
        <SectionContent>
          <p>
            Algolyze itself is <strong>free to use</strong>. We build automated export jobs, custom
            dashboards, data pipelines, and full Algolia implementations for teams that need more
            than the free toolkit.
          </p>
          <ServicesList>
            <ServiceTag>automated export jobs</ServiceTag>
            <ServiceTag>custom dashboards</ServiceTag>
            <ServiceTag>audit & consulting</ServiceTag>
            <ServiceTag>algolia implementation</ServiceTag>
            <ServiceTag>search optimization</ServiceTag>
            <ServiceTag>dedicated support</ServiceTag>
          </ServicesList>
          <ContactInfo>
            <div>
              <div className="name">Antonin Coste</div>
              <div className="email">
                <a href="mailto:antonin@internetcompany.fr">antonin@internetcompany.fr</a>
              </div>
            </div>
            <ContactButton href="mailto:antonin@internetcompany.fr">Get in touch</ContactButton>
          </ContactInfo>
        </SectionContent>
      </ContactCard>
    </HomeContainer>
  );
};

export default Home;